import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';

import { H7 } from '@/components/type';
import { getRelevantPriceForVehicle } from '@/utils/get-relevant-price-for-vehicle';
import {
  StyledCellText,
  StyledItemCell,
  StyledHeaderCell,
  StyledItemRow,
  StyledTableBody,
  StyledTableContainer,
  StyledTableHead,
  StyledVehicleDescriptionHeader,
} from './styles';
import Flex from '@/components/grid/flex';
import { type HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';

export type VehicleVariantOption = {
  id: string;
  value: string;
  label?: string;
  newPrice?: number | null;
  privateMinPrice?: number | null;
  privateMaxPrice?: number | null;
  safetyFeaturesLinkedCount?: number | null;
  totalRecommendedSafetyFeatures?: number | null;
};

type VehicleVariantListProps = {
  label?: string;
  options: VehicleVariantOption[];
  selectedOption?: VehicleVariantOption;
  allVehicles: HsiycSearchVehiclesRecord[];
  onChange: (option: VehicleVariantOption) => void;
};

const VehicleVariantList: React.FC<VehicleVariantListProps> = ({
  options,
  selectedOption = { id: '', value: '', label: '' },
  allVehicles,
  onChange,
}) => {
  const extendedOptions = options.map((option) => {
    const matchingVehicleRecord = allVehicles.filter((vehicle) => vehicle.redbookCode === option.id);
    return {
      ...option,
      value: option.id,
      label: option.value,
      newPrice: matchingVehicleRecord[0]?.newPrice,
      privateMinPrice: matchingVehicleRecord[0]?.privateMinPrice,
      privateMaxPrice: matchingVehicleRecord[0]?.privateMaxPrice,
      safetyFeaturesLinkedCount: matchingVehicleRecord[0]?.safetyFeaturesLinkedCount,
      totalRecommendedSafetyFeatures: matchingVehicleRecord[0]?.totalRecommendedSafetyFeatures,
    };
  });

  const handleClick = (value: string): void => {
    const updatedOption = extendedOptions.find((option) => option.id === value);
    if (updatedOption) {
      onChange(updatedOption);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <StyledTableContainer>
      <Table>
        <StyledTableHead>
          <TableRow>
            <StyledHeaderCell>
              <StyledVehicleDescriptionHeader as="h2">Vehicle Description</StyledVehicleDescriptionHeader>
            </StyledHeaderCell>
            {!isMobile && (
              <>
                <StyledHeaderCell align="right">
                  <H7 as="h3">Recommended Safety Features</H7>
                </StyledHeaderCell>
                <StyledHeaderCell align="right">
                  <H7 as="h3">Price Guide</H7>
                </StyledHeaderCell>
              </>
            )}
          </TableRow>
        </StyledTableHead>
        <StyledTableBody>
          {extendedOptions.map((option) => (
            <StyledItemRow
              hover
              key={option.value}
              onClick={() => handleClick(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(option.value);
                }
              }}
              selected={option.value === selectedOption.id}
              aria-label={option.label}
              role="button"
              tabIndex={0}
            >
              <StyledItemCell>
                <StyledCellText>{option.label}</StyledCellText>
              </StyledItemCell>
              {isMobile ? (
                <StyledItemCell>
                  <Flex flexDirection="column" gap="1rem" className="mobile-variant-list-cell">
                    <StyledCellText>
                      {getRelevantPriceForVehicle({
                        newPrice: option.newPrice,
                        privateMaxPrice: option.privateMaxPrice,
                        privateMinPrice: option.privateMinPrice,
                      })}
                    </StyledCellText>
                    <StyledCellText>
                      {option.safetyFeaturesLinkedCount}/{option.totalRecommendedSafetyFeatures}
                    </StyledCellText>
                  </Flex>
                </StyledItemCell>
              ) : (
                <>
                  <StyledItemCell align="right">
                    <StyledCellText>
                      {option.safetyFeaturesLinkedCount}/{option.totalRecommendedSafetyFeatures}
                    </StyledCellText>
                  </StyledItemCell>
                  <StyledItemCell align="right">
                    <StyledCellText>
                      {getRelevantPriceForVehicle({
                        newPrice: option.newPrice,
                        privateMaxPrice: option.privateMaxPrice,
                        privateMinPrice: option.privateMinPrice,
                      })}
                    </StyledCellText>
                  </StyledItemCell>
                </>
              )}
            </StyledItemRow>
          ))}
        </StyledTableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default VehicleVariantList;
